import type { BaseModelAttributes } from './base';
import { I18nLabels } from './common';
import { Locale } from './locale';

export enum CoinSourceType {
  DAILY_SIGNIN = 'daily_signin',
  MONTHLY_CARD = 'monthly_card',
  ADMIN_GRANT = 'admin_grant',
  TASK_REWARD = 'task_reward',
  REFUND = 'refund',
  VIDEO_GENERATION = 'video_generation',
  IMAGE_GENERATION = 'image_generation',
  IMAGE_EDIT = 'image_edit',
  AUDIO_GENERATION = 'audio_generation',
  MUSIC_GENERATION = 'music_generation',
  VIDEO_LIPSYNC = 'video_lipsync',
  EPISODE_SCRIPT = 'episode_script',
  ASSET_GENERATION = 'asset_generation',
  PROJECT_SETTING_GENERATION = 'project_setting_generation',
  LYRICS_GENERATION = 'lyrics_generation',
  MUSIC_EDITING = 'music_editing',
  AUDIO_EDITING = 'audio_editing',
  TENANT_ADMIN_GRANT = 'tenant_admin_grant',
  TENANT_ADMIN_TRANSFER = 'tenant_admin_transfer',
  CREATIVE_CHAT = 'creative_chat',
  AVATAR_TRAIN = 'avatar_train',
  AVATAR_VIDEO = 'avatar_video',
}

export const CoinSourceTypeLabels: I18nLabels<CoinSourceType> = {
  [CoinSourceType.DAILY_SIGNIN]: { [Locale.ZH_CN]: '每日签到', [Locale.EN_US]: 'Daily Sign-in' },
  [CoinSourceType.MONTHLY_CARD]: { [Locale.ZH_CN]: '月卡', [Locale.EN_US]: 'Monthly Card' },
  [CoinSourceType.ADMIN_GRANT]: { [Locale.ZH_CN]: '管理员发放', [Locale.EN_US]: 'Admin Grant' },
  [CoinSourceType.TASK_REWARD]: { [Locale.ZH_CN]: '任务奖励', [Locale.EN_US]: 'Task Reward' },
  [CoinSourceType.REFUND]: { [Locale.ZH_CN]: '退款', [Locale.EN_US]: 'Refund' },
  [CoinSourceType.VIDEO_GENERATION]: { [Locale.ZH_CN]: '视频生成', [Locale.EN_US]: 'Video Generation' },
  [CoinSourceType.IMAGE_GENERATION]: { [Locale.ZH_CN]: '图片生成', [Locale.EN_US]: 'Image Generation' },
  [CoinSourceType.IMAGE_EDIT]: { [Locale.ZH_CN]: '图片编辑', [Locale.EN_US]: 'Image Edit' },
  [CoinSourceType.AUDIO_GENERATION]: { [Locale.ZH_CN]: '音频生成', [Locale.EN_US]: 'Audio Generation' },
  [CoinSourceType.MUSIC_GENERATION]: { [Locale.ZH_CN]: '音乐生成', [Locale.EN_US]: 'Music Generation' },
  [CoinSourceType.VIDEO_LIPSYNC]: { [Locale.ZH_CN]: '视频配音', [Locale.EN_US]: 'Video Lip Sync' },
  [CoinSourceType.EPISODE_SCRIPT]: { [Locale.ZH_CN]: '剧本生成', [Locale.EN_US]: 'Script Generation' },
  [CoinSourceType.ASSET_GENERATION]: { [Locale.ZH_CN]: '主体生成', [Locale.EN_US]: 'Asset Generation' },
  [CoinSourceType.PROJECT_SETTING_GENERATION]: { [Locale.ZH_CN]: '项目设定生成', [Locale.EN_US]: 'Project Setting Gen' },
  [CoinSourceType.LYRICS_GENERATION]: { [Locale.ZH_CN]: '歌词生成', [Locale.EN_US]: 'Lyrics Generation' },
  [CoinSourceType.MUSIC_EDITING]: { [Locale.ZH_CN]: '音乐编辑', [Locale.EN_US]: 'Music Editing' },
  [CoinSourceType.AUDIO_EDITING]: { [Locale.ZH_CN]: '音频编辑', [Locale.EN_US]: 'Audio Editing' },
  [CoinSourceType.TENANT_ADMIN_GRANT]: { [Locale.ZH_CN]: '租户管理员发放', [Locale.EN_US]: 'Tenant Admin Grant' },
  [CoinSourceType.TENANT_ADMIN_TRANSFER]: { [Locale.ZH_CN]: '租户管理员转移', [Locale.EN_US]: 'Tenant Admin Transfer' },
  [CoinSourceType.CREATIVE_CHAT]: { [Locale.ZH_CN]: '创意聊天', [Locale.EN_US]: 'Creative Chat' },
  [CoinSourceType.AVATAR_TRAIN]: { [Locale.ZH_CN]: '数字人训练', [Locale.EN_US]: 'Avatar Training' },
  [CoinSourceType.AVATAR_VIDEO]: { [Locale.ZH_CN]: '数字人视频', [Locale.EN_US]: 'Avatar Video' },
};

export enum CoinTransactionType {
  EARN = 'earn',
  CONSUME = 'consume',
  REFUND = 'refund',
  EXPIRE = 'expire'
}

export const CoinTransactionTypeLabels: I18nLabels<CoinTransactionType> = {
  [CoinTransactionType.EARN]: { [Locale.ZH_CN]: '获得', [Locale.EN_US]: 'Earned' },
  [CoinTransactionType.CONSUME]: { [Locale.ZH_CN]: '消费', [Locale.EN_US]: 'Consumed' },
  [CoinTransactionType.REFUND]: { [Locale.ZH_CN]: '退款', [Locale.EN_US]: 'Refunded' },
  [CoinTransactionType.EXPIRE]: { [Locale.ZH_CN]: '过期', [Locale.EN_US]: 'Expired' },
};

export interface ICoinAccount extends BaseModelAttributes {
  userId: string;
  balance: number;
  totalEarned: number;
  totalConsumed: number;
  totalExpired: number;
}

export interface ICoinBatch extends BaseModelAttributes {
  userId: string;
  sourceType: CoinSourceType;
  sourceId?: string;
  amount: number;
  remaining: number;
  expiresAt: Date;
  isExpired: boolean;
}

export interface ICoinTransaction extends BaseModelAttributes {
  userId: string;
  type: CoinTransactionType;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  sourceType: CoinSourceType;
  sourceId?: string;
  batchId?: string;
  relatedTransactionId?: string;
  description: string;
}

export interface EarnCoinsRequest {
  amount: number;
  sourceType: CoinSourceType;
  sourceId?: string;
  expiresAt: Date;
  description?: string;
}

export interface ConsumeCoinsRequest {
  amount: number;
  sourceType: CoinSourceType;
  sourceId: string;
  description?: string;
}

export interface RefundCoinsRequest {
  transactionId: string;
  description?: string;
}

export interface AdminGrantCoinsRequest {
  userId: string;
  amount: number;
  expiresAt: Date;
  description?: string;
}

export interface CoinTransactionQuery {
  userId?: string;
  type?: CoinTransactionType;
  sourceType?: CoinSourceType;
  startDate?: string;
  endDate?: string;
}
